import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, CheckCircle2, Share } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) {
    return (
      <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-emerald-600" data-testid="app-installed">
        <CheckCircle2 className="w-4 h-4" /> App Installed
      </span>
    );
  }

  // Don't render a misleading control where install isn't available.
  if (!deferred && !ios) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch (_) {}
      setDeferred(null);
    } else if (ios) {
      setIosOpen(true);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={onClick} className="gap-1.5" data-testid="install-pwa-button">
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">Install WiseAce Invoicing</span>
        <span className="sm:hidden">Install</span>
      </Button>
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle>Install WiseAce Invoicing</DialogTitle>
            <DialogDescription>Add WiseAce Invoicing to your Home Screen</DialogDescription>
          </DialogHeader>
          <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
            <li>Tap the <b>Share</b> icon <Share className="inline w-4 h-4 align-text-bottom" /> in Safari's toolbar.</li>
            <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
            <li>Tap <b>Add</b> — WiseAce Invoicing will appear on your Home Screen.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
